import { useState } from 'react';

import { saleorAuthClient } from '@/graphql/client';
import {
  DETAIL_PAGE_LARGE_IMAGE_SIZE,
  NEXT_PUBLIC_IMAGES_FORMAT,
  THUMBNAIL_IMAGE_SIZE,
} from '@/lib/const';
import { paths } from '@/lib/paths';
import { getWatchDetailWithoutChannel } from '@/lib/service/api';
import type {
  WatchRegisterParams,
  WatchRegisterResponse,
} from '@/lib/service/type';
import type { RegionsProviderContext } from '@/providers/RegionProvider';
import type { Watch } from '@/views/MyWatchesView';

export interface watchProps {
  channel: string;
  country: string;
  model: string;
  product_name?: string;
  sec?: string;
  sn: string;
}
export interface AddWatchPropsByBackOffice extends watchProps {
  uid: string;
}
export type ApiResponse = {
  code: number;
  data?: Watch | Watch[];
  message?: string;
  msg?: string;
};

export type ApiResponseBlob = {
  size: number;
  type: string;
};
export interface renderData {
  loading: boolean;
  watches: Watch[] | null;
}

function isUrlEncoded(str: string): boolean {
  try {
    return str !== decodeURIComponent(str);
  } catch (e) {
    return false;
  }
}

export const useMyWatches = (authClient = saleorAuthClient) => {
  const [loading, setLoading] = useState(false);

  const getWatchesList = async (): Promise<ApiResponse> => {
    try {
      setLoading(true);
      const response = await authClient.fetchWithAuth(
        paths.api.myWatches,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      setLoading(false);
      return {
        code: -1,
        msg: 'Failed to get the subscribe status of myWatches',
      };
    }
  };
  const getWatchBySaleor = async (
    data: Watch[],
    Provider: RegionsProviderContext,
  ) => {
    setLoading(true);
    const render: renderData = {
      watches: [],
      loading: false,
    };
    const watchesList: Watch[] = data;
    const arr = data.map((item: { sku: string }) => item.sku);
    try {
      const watchDetailsResult = await getWatchDetailWithoutChannel({
        skus: arr,
        first: 100,
        thumbnailSize: THUMBNAIL_IMAGE_SIZE,
        imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        largeMediaSize: DETAIL_PAGE_LARGE_IMAGE_SIZE,
        languageCode: Provider.query.languageCode,
      });
      const data = watchDetailsResult?.data;
      const watchs = watchesList.map((dataitem) => {
        let obj = dataitem;
        const haveObj = data?.watchs?.edges.filter(
          (item) => item.node.sku == obj.sku,
        );
        if (haveObj && haveObj?.length > 0) {
          const product = haveObj[0].node.product;
          obj = {
            ...product,
            ...obj,
          };
        }
        return obj;
      });
      render.watches = watchs;
      render.loading = loading;
      setLoading(loading);
      return render;
    } catch (error) {
      //TODO add error toast
      setLoading(false);
    }
    return render;
  };
  const addWatchToAccount = async (watch: watchProps) => {
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.myWatches,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify(watch),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      //TODO add error toast
    }
  };
  const getWatchDetail = async (
    sn: string,
    sku: string,
    Provider?: RegionsProviderContext,
  ) => {
    try {
      const model = sku.substring(0, 6);
      const response = await authClient.fetchWithAuth(
        paths.api.myWarranty + `?sn=${sn}&model=${model}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      const res = await response.json();
      if (res.code !== 200) {
        throw res;
      }
      if (res.data && res.data.sku && Provider) {
        const product = await getWatchBySkuForSaleor(res.data.sku, Provider);
        if (
          product?.watchs?.edges &&
          product.watchs?.edges.length > 0 &&
          res.data
        ) {
          return {
            data: { ...res.data, ...product.watchs.edges[0].node.product },
          };
        }
      }
      if (res) return res;
      return null;
    } catch (error) {
      return error;
    }
  };
  const getWatchBySkuForSaleor = async (
    sku: string,
    Provider: RegionsProviderContext,
  ) => {
    try {
      const watchDetailsResult = await getWatchDetailWithoutChannel({
        skus: [sku],
        first: 1,
        thumbnailSize: THUMBNAIL_IMAGE_SIZE,
        imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        largeMediaSize: DETAIL_PAGE_LARGE_IMAGE_SIZE,
        languageCode: Provider.query.languageCode,
      });
      return watchDetailsResult?.data;
    } catch (error) {
      //TODO add error toast
    }
  };
  const removeWatch = async (sn: string) => {
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.myWatches + `${sn}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );

      return response.json();
    } catch (error) {
      //TODO add error toast
    }
  };
  const getWatchWarrantyDetail = async (sn: string) => {
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.myWatches + `${sn}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Watch not found',
      };
    }
  };
  const getWatchWarranty = async (sn: string, sec: string, channel: string) => {
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.myWarranty + `?sn=${sn}&sec=${sec}&channel=${channel}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Watch not found',
      };
    }
  };
  const getCertificates = async (sn: string, type: string, channel: string) => {
    try {
      const response = await authClient.fetchWithAuth(
        `${paths.api.myWatches}${sn}/certificates/${type}?channel=${channel}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.blob();
    } catch (error) {
      return {
        code: -1,
        msg: 'error',
      };
    }
  };

  const getNftInfo = async (sn: string, sec?: string) => {
    try {
      const response = await authClient.fetchWithAuth(
        `${paths.api.nft}?sn=${sn}${sec ? `&sec=${sec}` : ''}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );

      return await response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get the nft',
      };
    }
  };

  const getNftCTA = async (sn: string, sec?: string) => {
    try {
      const response = await authClient.fetchWithAuth(
        `${paths.api.nft}/owner/details?sn=${sn}${sec ? `&sec=${sec}` : ''}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );

      return await response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get the nft CTA',
      };
    }
  };

  const claimNFT = async (sn: string, sec: string) => {
    try {
      const obj = {
        sn,
        sec,
      };
      const response = await authClient.fetchWithAuth(
        `${paths.api.nft}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(obj),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return await response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to claim the nft',
      };
    }
  };
  const getNftHistory = async (sn: string, sec: string) => {
    try {
      const response = await authClient.fetchWithAuth(
        `${paths.api.nft}history?sn=${sn}&sec=${sec}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get nft history',
      };
    }
  };
  const creatNftProof = async (sn: string, sec: string) => {
    try {
      const obj = {
        sn,
        sec,
      };
      const response = await authClient.fetchWithAuth(
        `${paths.api.nft}proof`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(obj),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get nft history',
      };
    }
  };

  const checkWatchExtendable = async (sn: string, checkoutId: string) => {
    try {
      const obj = {
        sn,
        checkout_id: checkoutId,
      };
      const response = await authClient.fetchWithAuth(
        `${paths.api.extendable}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(obj),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get watch extendable status',
      };
    }
  };

  const unclaimNft = async (sn: string, sec: string, email: string) => {
    try {
      const encoded_email = isUrlEncoded(email)
        ? email
        : encodeURIComponent(email);
      const response = await authClient.fetchWithAuth(
        `${paths.api.nft}?sn=${sn}&sec=${sec}&email=${encoded_email}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to unclaim nft',
      };
    }
  };
  const addWatchToAccountByMyAccount = async (
    params: WatchRegisterParams,
  ): Promise<WatchRegisterResponse | null> => {
    const response = await fetch(paths.api.watch.watchRegister.asPath(), {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return response.json();
  };
  return {
    addWatchToAccountByMyAccount,
    claimNFT,
    checkWatchExtendable,
    getWatchesList,
    addWatchToAccount,
    getWatchDetail,
    removeWatch,
    getWatchBySkuForSaleor,
    getWatchBySaleor,
    getWatchWarrantyDetail,
    getWatchWarranty,
    getCertificates,
    getNftInfo,
    getNftCTA,
    getNftHistory,
    creatNftProof,
    unclaimNft,
    loading,
  };
};
